import { supabase } from '../../lib/supabase';
import { setActiveWorkspaceOrgId } from './activeWorkspaceOrgId';
import { setWorkspaceOrg } from './saasOrg';

export type CustomerAccountScope = 'none' | 'this_org' | 'other_org';

export const CUSTOMER_SAME_ORG_ACCOUNT_MESSAGE =
  'You already have an account with this organisation. Sign in with the password you created for it.';

/**
 * Auth emails are unique across ServiceApp. A second organisation gets its own
 * login address so the customer can choose a new password. The profile keeps
 * the real email they typed.
 */
export function orgScopedCustomerAuthEmail(organizationId: string, contactEmail: string): string {
  const orgKey = organizationId.replace(/-/g, '').slice(0, 12).toLowerCase();
  const normalized = contactEmail.trim().toLowerCase();
  const at = normalized.lastIndexOf('@');
  const local = (at > 0 ? normalized.slice(0, at) : 'customer').replace(/[^a-z0-9._+-]/g, '') || 'customer';
  const domain = at > 0 && normalized.slice(at + 1) ? normalized.slice(at + 1) : 'serviceapp.ai';
  return `${local}+org.${orgKey}@${domain}`;
}

/** Shown when this organisation already has the customer. Another organisation is a new account. */
export function existingLoginNotice(scope: CustomerAccountScope, orgName: string): string | null {
  if (scope !== 'this_org') return null;
  const name = orgName.trim() || 'this organisation';
  return `You already have an account with ${name}. Sign in with the password you created for this organisation.`;
}

export type CustomerLoginIdentity = {
  authEmail: string;
  isActive: boolean;
};

/** The auth login for this organisation, which may differ from the email the customer types. */
export async function resolveCustomerLoginIdentity(
  email: string,
  phone: string | null,
  organizationId: string,
): Promise<CustomerLoginIdentity | null> {
  const { data, error } = await supabase.rpc('saas_resolve_customer_login', {
    p_email: email,
    p_phone: phone ?? '',
    p_organization_id: organizationId,
  });
  if (error || data == null) return null;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { auth_email?: unknown; is_active?: unknown }
    | null
    | undefined;
  if (!row || typeof row.auth_email !== 'string' || !row.auth_email.trim()) return null;
  return { authEmail: row.auth_email.trim().toLowerCase(), isActive: row.is_active !== false };
}

export function parseCustomerAccountScope(value: unknown): CustomerAccountScope {
  if (value === 'this_org' || value === 'other_org' || value === 'none') return value;
  return 'none';
}

/**
 * Use a saved workspace when this person is a customer of that organisation,
 * even if their staff membership is somewhere else.
 */
export function persistedCustomerOrgId(
  persistedOrgId: string | null | undefined,
  membershipOrgIds: string[],
  customerOrgIds: string[],
): string | null {
  const persisted = persistedOrgId?.trim() || '';
  if (!persisted) return null;
  if (membershipOrgIds.includes(persisted)) return null;
  return customerOrgIds.includes(persisted) ? persisted : null;
}

export async function lookupCustomerAccountScope(
  email: string,
  phone: string | null,
  organizationId: string,
): Promise<CustomerAccountScope> {
  const { data, error } = await supabase.rpc('saas_customer_account_scope', {
    p_email: email,
    p_phone: phone ?? '',
    p_organization_id: organizationId,
  });
  if (error) return 'none';
  return parseCustomerAccountScope(data);
}

/** Record this signed-in person as a customer of the organisation and open that workspace. */
export async function attachSignedInCustomerToOrganization(organizationId: string): Promise<void> {
  const { error } = await supabase.rpc('saas_join_organization_as_customer', {
    p_organization_id: organizationId,
  });
  if (error) throw new Error(error.message);
  setActiveWorkspaceOrgId(organizationId);
  await setWorkspaceOrg(organizationId);
}
